import { css, styled } from "@mui/material";
import { Box, colors } from "@procore/core-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Events, track } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import { Window } from "components";
import { ListNode } from "components/objectTree/types";
import { collapseAllNodes } from "components/objectTree/utils";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { highlightActions } from "contexts/highlighted";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { BaseWindowProps } from "contexts/windows/reducer";
import { ModelTree } from "features/modelTree/components/modelTree";
import { emptyFileTypeFilters } from "features/modelTree/constants";
import { ModelTreeNodeData } from "features/modelTree/types";
import { useAbortController } from "hooks/useAbortController";
import { useObjectTreeSearch } from "hooks/useObjectTreeSearch";
import { selectIsOnline } from "slices/explorer";
import { telemetry } from "telemetry";
import { selectObjectHierarchy, selectObjectHierarchyFromFilteredTree } from "utils/objectActions";
import { getGroupStateForFilteredTree, getGroupStateForIdSet } from "utils/treeHelpers";

import { useBulkActionsContextMenu } from "../hooks/useBulkActionsContextMenu";
import { ObjectTreeControlsBar } from "./components/ObjectTreeControlsBar";
import { ObjectTreeNodeProcore } from "./components/ObjectTreeNodeProcore";
import { ITEM_HEIGHT } from "./constants";

const ObjectTreeWindowContent = styled(Box)(
    () => css`
        height: 100%;
        overflow: hidden;

        .object-tree-scroll-container {
            overflow: auto;
            overscroll-behavior-x: contain;
            touch-action: pan-x pan-y;
            scrollbar-gutter: stable;
            scrollbar-color: transparent transparent;

            &:hover {
                scrollbar-color: ${colors.gray70} transparent;
            }
        }
    `,
);

export function ObjectTreeWindow({ onClose, position = "center", ...props }: BaseWindowProps) {
    const { t } = useTranslation();
    const {
        state: { db },
    } = useExplorerGlobals();
    const isOnline = useAppSelector(selectIsOnline);
    const highlightedSet = useHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();

    const {
        searchTerm,
        filteredObjectIds,
        tree,
        setTree,
        treeKey,
        isSearching,
        hasNoResults,
        searchError,
        isFilterActive,
        handleSearchChange,
        handleSearchSubmit,
    } = useObjectTreeSearch(db);

    const [selectionAbortController] = useAbortController();

    const { openContextMenu, contextMenuNode } = useBulkActionsContextMenu();

    const onNodeRightClick = useCallback(
        (x: number, y: number, item: ListNode<ModelTreeNodeData>) => {
            const selected =
                filteredObjectIds != null
                    ? getGroupStateForFilteredTree(highlightedSet.ids, item, filteredObjectIds, tree)
                    : getGroupStateForIdSet(highlightedSet.ids, item, tree);

            const isAlreadySelected = selected !== "none";

            // If node is NOT already selected, clear and select it (async - will complete in background)
            if (!isAlreadySelected && db) {
                dispatchHighlighted(highlightActions.setIds([]));

                const objects = item.nodeGroup?.map((n) => n.data.object) ?? [item.node.data.object];

                // Note: dispatchHidden is intentionally NOT passed to selection functions.
                // Objects can exist in both selected and hidden sets simultaneously.
                const selectionPromise =
                    filteredObjectIds != null
                        ? selectObjectHierarchyFromFilteredTree({
                              nodes: objects,
                              filteredObjectIds,
                              dispatchHighlighted,
                              tree,
                          })
                        : selectObjectHierarchy({
                              nodes: objects,
                              db,
                              abortSignal: selectionAbortController.current.signal,
                              setLoading: () => {},
                              dispatchHighlighted,
                          });

                selectionPromise.catch((error) => {
                    if (!selectionAbortController.current.signal.aborted) {
                        console.error("Context menu selection failed:", error);
                        telemetry.trackCaughtException({
                            source: "objectTreeWindow.contextMenuSelection",
                            category: "data",
                            error,
                            severity: "error",
                        });
                    }
                });
            }

            openContextMenu(x, y);
        },
        [
            db,
            filteredObjectIds,
            tree,
            highlightedSet.ids,
            dispatchHighlighted,
            selectionAbortController,
            openContextMenu,
        ],
    );

    const handleCollapseAll = useCallback(() => {
        setTree((currentTree) => collapseAllNodes(currentTree));
        void track(Events.objectTree.collapseAll);
    }, [setTree]);

    const emptyState = !isOnline
        ? {
              title: t("noInternetConnection"),
              message: t("objectTreeOffline"),
              showIcon: false,
          }
        : searchError
          ? {
                title: t("objectTreeSearchError"),
                message: searchError,
            }
          : hasNoResults
            ? {
                  title: t("noResultsFound"),
                  message: t("tryDifferentSearchTerm"),
              }
            : undefined;

    return (
        <Window
            position={position}
            size={{ width: 400, height: 600 }}
            loading={isSearching}
            loadingMessage={t("loadingResults")}
            emptyState={emptyState}
            title={t("objectTree")}
            onClose={onClose}
            search={{
                value: searchTerm,
                onChange: handleSearchChange,
                placeholder: t("filterByKeyword"),
                onSubmit: handleSearchSubmit,
            }}
            bulkActions={
                isOnline
                    ? {
                          active: highlightedSet.idArr.length > 0,
                          children: (
                              <ObjectTreeControlsBar
                                  onCollapseAll={handleCollapseAll}
                                  tree={tree}
                                  filteredObjectIds={filteredObjectIds}
                                  hasNoResults={hasNoResults}
                                  unwrapped
                              />
                          ),
                      }
                    : undefined
            }
            data-testid="model-tree-window"
            {...props}
        >
            <ObjectTreeWindowContent>
                <ModelTree
                    key={treeKey}
                    tree={tree}
                    setTree={setTree}
                    fileTypes={emptyFileTypeFilters}
                    NodeRenderer={ObjectTreeNodeProcore}
                    itemSize={ITEM_HEIGHT}
                    filteredObjectIds={filteredObjectIds}
                    searchTerm={searchTerm}
                    onNodeRightClick={onNodeRightClick}
                    isFilterActive={isFilterActive}
                />
            </ObjectTreeWindowContent>

            {contextMenuNode}
        </Window>
    );
}
